import { useState, useRef, useEffect } from "react";

// ═══════════════════════════════════════════════════════════════
// DONATE — 50% keeps the tool free. 50% goes to a pet in need.
// ═══════════════════════════════════════════════════════════════

function useInView(ref, threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

function Fade({ children, delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref);
  return (
    <div ref={ref} style={{
      opacity: v ? 1 : 0,
      transform: v ? "translateY(0)" : "translateY(24px)",
      transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
    }}>{children}</div>
  );
}

const AMOUNTS = [5, 10, 25, 50, 100];

export default function DonatePage() {
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [showNominationForm, setShowNominationForm] = useState(false);
  const [formData, setFormData] = useState({
    nominatorName: "",
    nominatorEmail: "",
    recipientName: "",
    recipientContact: "",
    petName: "",
    petSpecies: "Dog",
    petCondition: "",
    story: "",
    charityName: "",
    charityUrl: "",
    nominationType: "family",
  });
  const [submitted, setSubmitted] = useState(false);
  const [donateClicked, setDonateClicked] = useState(false);

  const activeAmount = isCustom ? (parseFloat(customAmount) || 0) : selectedAmount;
  const petHalf = (activeAmount / 2).toFixed(2);
  const toolHalf = (activeAmount / 2).toFixed(2);

  const handleField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNominationSubmit = () => {
    // In production this would POST to your backend / Airtable / Google Form
    console.log("Nomination submitted:", formData);
    setSubmitted(true);
  };

  const handleDonate = () => {
    window.open("https://www.paypal.com/ncp/payment/373PV6794CYBL", "_blank");
    setDonateClicked(true);
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(170deg, #0a0a0c 0%, #0c1018 40%, #0a0f1a 100%)",
      color: "#d4cdc4",
      fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Lora:ital,wght@0,400;0,500;1,400&display=swap');
        ::selection { background: rgba(180, 140, 100, 0.3); color: #f0e6d8; }
        @keyframes gentleGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(180,140,100,0.08); }
          50% { box-shadow: 0 0 40px rgba(180,140,100,0.15); }
        }
        input:focus, textarea:focus, select:focus {
          outline: none;
          border-color: rgba(180,140,100,0.4) !important;
        }
      `}</style>

      {/* ════ HEADER ════ */}
      <div style={{
        padding: "48px 24px 40px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at 50% 30%, rgba(180,140,100,0.04) 0%, transparent 60%)",
        }} />
        <div style={{ position: "relative", maxWidth: 600, margin: "0 auto" }}>
          <div style={{
            fontSize: 17, letterSpacing: "0.35em", textTransform: "uppercase",
            color: "rgba(180, 140, 100, 0.5)", marginBottom: 20,
            fontFamily: "'Lora', serif",
          }}>
            Support the Mission
          </div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(32px, 7vw, 52px)",
            fontWeight: 300, lineHeight: 1.2,
            color: "#e8ddd0", margin: "0 0 20px",
          }}>
            Every Dollar is <span style={{ fontStyle: "italic", color: "#c4a87c" }}>Split in Half</span>
          </h1>
          <div style={{ width: 40, height: 1, background: "rgba(180,140,100,0.4)", margin: "0 auto 20px" }} />
          <p style={{
            fontFamily: "'Lora', serif", fontSize: 17, lineHeight: 1.8,
            color: "rgba(200, 190, 175, 0.7)", maxWidth: 460, margin: "0 auto",
          }}>
            50% keeps this tool free and growing. 50% goes into our Pet Relief Fund — 
            pooled together and directed to verified pets and families in real need, 
            chosen from community nominations.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "0 20px 80px" }}>

        {/* ════ THE SPLIT VISUAL ════ */}
        <Fade>
          <div style={{
            display: "flex", gap: 12, marginBottom: 32,
          }}>
            <div style={{
              flex: 1, padding: "20px 16px", borderRadius: 12, textAlign: "center",
              background: "rgba(56,189,248,0.04)", border: "1px solid rgba(56,189,248,0.12)",
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🛠️</div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 26, fontWeight: 300, color: "#7dd3fc", marginBottom: 4,
              }}>50%</div>
              <div style={{
                fontFamily: "'Lora', serif", fontSize: 26,
                color: "rgba(125,211,252,0.6)", lineHeight: 1.5,
              }}>
                Keeps the Supplement Engine free, the recall tracker running, and the database growing
              </div>
            </div>
            <div style={{
              flex: 1, padding: "20px 16px", borderRadius: 12, textAlign: "center",
              background: "rgba(180,140,100,0.04)", border: "1px solid rgba(180,140,100,0.12)",
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🐾</div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 26, fontWeight: 300, color: "#c4a87c", marginBottom: 4,
              }}>50%</div>
              <div style={{
                fontFamily: "'Lora', serif", fontSize: 26,
                color: "rgba(196,168,124,0.6)", lineHeight: 1.5,
              }}>
                Goes into our Pet Relief Fund — pooled donations directed to verified pets and families in need
              </div>
            </div>
          </div>
        </Fade>

        {/* ════ AMOUNT SELECTION ════ */}
        <Fade delay={0.1}>
          <div style={{
            padding: "28px 24px", borderRadius: 14,
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(180,140,100,0.1)",
            marginBottom: 24,
          }}>
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 26, fontWeight: 400, color: "#d4cdc4", marginBottom: 16, textAlign: "center",
            }}>
              Choose an Amount
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 16 }}>
              {AMOUNTS.map(amt => (
                <button key={amt} onClick={() => { setSelectedAmount(amt); setIsCustom(false); }} style={{
                  padding: "12px 20px", borderRadius: 10, border: "1px solid",
                  fontSize: 26, fontWeight: 500, cursor: "pointer", minWidth: 70,
                  fontFamily: "'Lora', serif",
                  background: (!isCustom && selectedAmount === amt) ? "rgba(180,140,100,0.15)" : "rgba(255,255,255,0.03)",
                  color: (!isCustom && selectedAmount === amt) ? "#c4a87c" : "#8b9aa8",
                  borderColor: (!isCustom && selectedAmount === amt) ? "rgba(180,140,100,0.4)" : "rgba(255,255,255,0.08)",
                  transition: "all 0.2s",
                }}>
                  ${amt}
                </button>
              ))}
              <button onClick={() => setIsCustom(true)} style={{
                padding: "12px 20px", borderRadius: 10, border: "1px solid",
                fontSize: 26, cursor: "pointer", minWidth: 70,
                fontFamily: "'Lora', serif",
                background: isCustom ? "rgba(180,140,100,0.15)" : "rgba(255,255,255,0.03)",
                color: isCustom ? "#c4a87c" : "#8b9aa8",
                borderColor: isCustom ? "rgba(180,140,100,0.4)" : "rgba(255,255,255,0.08)",
                transition: "all 0.2s",
              }}>
                Custom
              </button>
            </div>

            {isCustom && (
              <div style={{ marginBottom: 16, textAlign: "center" }}>
                <div style={{ position: "relative", display: "inline-block" }}>
                  <span style={{
                    position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                    fontSize: 26, color: "#c4a87c", fontFamily: "'Lora', serif",
                  }}>$</span>
                  <input
                    type="number" min="1" step="1"
                    value={customAmount}
                    onChange={e => setCustomAmount(e.target.value)}
                    placeholder="0"
                    style={{
                      ...inputStyle,
                      width: 140, textAlign: "center", paddingLeft: 28,
                      fontSize: 26, fontWeight: 500,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Live split preview */}
            {activeAmount > 0 && (
              <div style={{
                display: "flex", justifyContent: "center", gap: 24,
                padding: "12px 0", borderTop: "1px solid rgba(180,140,100,0.08)",
              }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 17, color: "rgba(125,211,252,0.5)", fontFamily: "'Lora', serif", marginBottom: 2 }}>
                    Tool &amp; Database
                  </div>
                  <div style={{ fontSize: 26, color: "#7dd3fc", fontFamily: "'Cormorant Garamond', serif" }}>
                    ${toolHalf}
                  </div>
                </div>
                <div style={{ width: 1, background: "rgba(180,140,100,0.15)" }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 17, color: "rgba(196,168,124,0.5)", fontFamily: "'Lora', serif", marginBottom: 2 }}>
                    Pet Relief Fund
                  </div>
                  <div style={{ fontSize: 26, color: "#c4a87c", fontFamily: "'Cormorant Garamond', serif" }}>
                    ${petHalf}
                  </div>
                </div>
              </div>
            )}

            {/* DONATE BUTTON */}
            <div style={{ textAlign: "center", marginTop: 16 }}>
              {!donateClicked ? (
                <button onClick={handleDonate} style={{
                  padding: "16px 48px", borderRadius: 10,
                  border: "1px solid rgba(180,140,100,0.35)",
                  background: "linear-gradient(135deg, rgba(180,140,100,0.12) 0%, rgba(180,140,100,0.06) 100%)",
                  color: "#c4a87c", fontSize: 26, fontWeight: 500,
                  fontFamily: "'Cormorant Garamond', serif",
                  letterSpacing: "0.05em", cursor: "pointer",
                  transition: "all 0.3s",
                  animation: "gentleGlow 4s ease-in-out infinite",
                }}>
                  Donate ${activeAmount > 0 ? activeAmount.toFixed(2) : "—"}
                </button>
              ) : (
                <div style={{
                  padding: "20px", borderRadius: 10,
                  background: "rgba(180,140,100,0.06)",
                  border: "1px solid rgba(180,140,100,0.15)",
                }}>
                  <div style={{ fontSize: 26, color: "#c4a87c", marginBottom: 8, fontFamily: "'Cormorant Garamond', serif" }}>
                    Thank you for your generosity
                  </div>
                  <div style={{ fontSize: 18, color: "rgba(200,190,175,0.5)", fontFamily: "'Lora', serif", lineHeight: 1.6 }}>
                    A PayPal window should have opened. If it didn't, <a href="https://www.paypal.com/ncp/payment/373PV6794CYBL" target="_blank" rel="noopener noreferrer" style={{ color: "#c4a87c", textDecoration: "underline" }}>click here to donate</a>.
                  </div>
                </div>
              )}
            </div>
          </div>
        </Fade>

        {/* ════ TRANSPARENCY NOTE ════ */}
        <Fade delay={0.15}>
          <div style={{
            padding: "16px 20px", borderRadius: 10, marginBottom: 32,
            background: "rgba(255,255,255,0.015)",
            border: "1px solid rgba(255,255,255,0.04)",
          }}>
            <div style={{
              fontFamily: "'Lora', serif", fontSize: 26,
              color: "rgba(200,190,175,0.45)", lineHeight: 1.7, textAlign: "center",
            }}>
              <strong style={{ color: "rgba(200,190,175,0.6)" }}>Transparency note:</strong> This 
              project is not currently a registered 501(c)(3) nonprofit. Your donation is a gift, 
              not a tax-deductible charitable contribution. We believe in radical transparency — 
              every dollar's destination will be documented and shared with our community.
            </div>
          </div>
        </Fade>

        {/* ════ NOMINATION SECTION ════ */}
        <Fade delay={0.2}>
          <div style={{
            padding: "28px 24px", borderRadius: 14,
            background: "rgba(180,140,100,0.02)",
            border: "1px solid rgba(180,140,100,0.1)",
          }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 26, fontWeight: 300, color: "#e0d6c8", marginBottom: 8,
              }}>
                Know a Pet That Needs Help?
              </div>
              <div style={{
                fontFamily: "'Lora', serif", fontSize: 17,
                color: "rgba(200,190,175,0.55)", lineHeight: 1.7, maxWidth: 420, margin: "0 auto",
              }}>
                Nominate a family struggling with vet bills, a local rescue, or a charity doing real work. 
                We review every nomination and direct pooled funds where they'll make the most difference — 
                because $10 alone can't change a life, but $500 from a community can.
              </div>
            </div>

            {!showNominationForm && !submitted && (
              <div style={{ textAlign: "center" }}>
                <button onClick={() => setShowNominationForm(true)} style={{
                  padding: "14px 36px", borderRadius: 10,
                  border: "1px solid rgba(180,140,100,0.25)",
                  background: "rgba(180,140,100,0.06)",
                  color: "#c4a87c", fontSize: 26,
                  fontFamily: "'Lora', serif",
                  cursor: "pointer", transition: "all 0.2s",
                }}>
                  Nominate a Pet or Charity →
                </button>
              </div>
            )}

            {showNominationForm && !submitted && (
              <div style={{ marginTop: 16 }}>
                {/* TYPE TOGGLE */}
                <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                  {[
                    { id: "family", label: "A Family / Pet in Need", icon: "🏠" },
                    { id: "charity", label: "A Charity or Rescue", icon: "🏥" },
                  ].map(t => (
                    <button key={t.id} onClick={() => handleField("nominationType", t.id)} style={{
                      flex: 1, padding: "12px 12px", borderRadius: 10,
                      border: "1px solid",
                      fontSize: 26, cursor: "pointer",
                      fontFamily: "'Lora', serif",
                      background: formData.nominationType === t.id ? "rgba(180,140,100,0.1)" : "rgba(255,255,255,0.02)",
                      color: formData.nominationType === t.id ? "#c4a87c" : "#8b9aa8",
                      borderColor: formData.nominationType === t.id ? "rgba(180,140,100,0.3)" : "rgba(255,255,255,0.06)",
                      transition: "all 0.2s",
                    }}>
                      {t.icon} {t.label}
                    </button>
                  ))}
                </div>

                {/* YOUR INFO */}
                <FieldLabel>Your Name</FieldLabel>
                <input style={inputStyle} value={formData.nominatorName}
                  onChange={e => handleField("nominatorName", e.target.value)}
                  placeholder="Your name" />

                <FieldLabel>Your Email</FieldLabel>
                <input style={inputStyle} type="email" value={formData.nominatorEmail}
                  onChange={e => handleField("nominatorEmail", e.target.value)}
                  placeholder="your@email.com" />

                <div style={{ height: 1, background: "rgba(180,140,100,0.08)", margin: "20px 0" }} />

                {formData.nominationType === "family" ? (
                  <>
                    <FieldLabel>Name of Person / Family</FieldLabel>
                    <input style={inputStyle} value={formData.recipientName}
                      onChange={e => handleField("recipientName", e.target.value)}
                      placeholder="Who needs help?" />

                    <FieldLabel>Best Way to Reach Them</FieldLabel>
                    <input style={inputStyle} value={formData.recipientContact}
                      onChange={e => handleField("recipientContact", e.target.value)}
                      placeholder="Phone, email, social media, etc." />

                    <FieldLabel>Pet's Name</FieldLabel>
                    <input style={inputStyle} value={formData.petName}
                      onChange={e => handleField("petName", e.target.value)}
                      placeholder="What's the pet's name?" />

                    <div style={{ display: "flex", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <FieldLabel>Species</FieldLabel>
                        <select style={{ ...inputStyle, appearance: "none" }} value={formData.petSpecies}
                          onChange={e => handleField("petSpecies", e.target.value)}>
                          <option value="Dog">Dog</option>
                          <option value="Cat">Cat</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div style={{ flex: 2 }}>
                        <FieldLabel>Condition / What They Need</FieldLabel>
                        <input style={inputStyle} value={formData.petCondition}
                          onChange={e => handleField("petCondition", e.target.value)}
                          placeholder="e.g. cancer treatment, surgery, medication" />
                      </div>
                    </div>

                    <FieldLabel>Their Story (optional but powerful)</FieldLabel>
                    <textarea style={{ ...inputStyle, minHeight: 100, resize: "vertical" }}
                      value={formData.story}
                      onChange={e => handleField("story", e.target.value)}
                      placeholder="Tell us about this pet and their family. What happened? What do they need? Why does this matter?" />
                  </>
                ) : (
                  <>
                    <FieldLabel>Charity / Rescue Name</FieldLabel>
                    <input style={inputStyle} value={formData.charityName}
                      onChange={e => handleField("charityName", e.target.value)}
                      placeholder="Organization name" />

                    <FieldLabel>Website or Social Media</FieldLabel>
                    <input style={inputStyle} value={formData.charityUrl}
                      onChange={e => handleField("charityUrl", e.target.value)}
                      placeholder="URL or social handle" />

                    <FieldLabel>Contact Person (if known)</FieldLabel>
                    <input style={inputStyle} value={formData.recipientContact}
                      onChange={e => handleField("recipientContact", e.target.value)}
                      placeholder="Name, email, or phone" />

                    <FieldLabel>Why This Organization? (optional)</FieldLabel>
                    <textarea style={{ ...inputStyle, minHeight: 100, resize: "vertical" }}
                      value={formData.story}
                      onChange={e => handleField("story", e.target.value)}
                      placeholder="Tell us about the work they do and why you believe in them." />
                  </>
                )}

                <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
                  <button onClick={() => setShowNominationForm(false)} style={{
                    flex: 1, padding: "14px", borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.06)",
                    background: "transparent",
                    color: "#6b7a88", fontSize: 17,
                    fontFamily: "'Lora', serif", cursor: "pointer",
                  }}>
                    Cancel
                  </button>
                  <button onClick={handleNominationSubmit} style={{
                    flex: 2, padding: "14px", borderRadius: 10,
                    border: "1px solid rgba(180,140,100,0.3)",
                    background: "rgba(180,140,100,0.1)",
                    color: "#c4a87c", fontSize: 26, fontWeight: 500,
                    fontFamily: "'Cormorant Garamond', serif",
                    cursor: "pointer", transition: "all 0.2s",
                  }}>
                    Submit Nomination
                  </button>
                </div>
              </div>
            )}

            {submitted && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>💛</div>
                <div style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 26, color: "#c4a87c", marginBottom: 8,
                }}>
                  Nomination Received
                </div>
                <div style={{
                  fontFamily: "'Lora', serif", fontSize: 17,
                  color: "rgba(200,190,175,0.5)", lineHeight: 1.6,
                }}>
                  Thank you for looking out for a pet in need. We review every 
                  nomination personally and add verified recipients to our 
                  distribution list. When pooled funds reach a meaningful amount, 
                  we'll reach out to coordinate.
                </div>
                <button onClick={() => { setSubmitted(false); setShowNominationForm(false); setFormData({
                  nominatorName:"",nominatorEmail:"",recipientName:"",recipientContact:"",
                  petName:"",petSpecies:"Dog",petCondition:"",story:"",charityName:"",
                  charityUrl:"",nominationType:"family",
                }); }} style={{
                  marginTop: 16, padding: "10px 24px", borderRadius: 8,
                  border: "1px solid rgba(180,140,100,0.15)",
                  background: "transparent",
                  color: "rgba(180,140,100,0.5)", fontSize: 26,
                  fontFamily: "'Lora', serif", cursor: "pointer",
                }}>
                  Nominate Another
                </button>
              </div>
            )}
          </div>
        </Fade>

        {/* ════ HOW IT WORKS ════ */}
        <Fade delay={0.25}>
          <div style={{ marginTop: 40, textAlign: "center" }}>
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 26, fontWeight: 300, color: "#d4cdc4", marginBottom: 24,
            }}>
              How It Works
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                { num: "1", title: "You Donate", desc: "Any amount. Every dollar is split 50/50 — half for the tool, half into the Pet Relief Fund." },
                { num: "2", title: "Anyone Can Nominate", desc: "Submit a family with vet bills, a rescue that needs supplies, or a charity doing real work for animals." },
                { num: "3", title: "We Review & Verify", desc: "Every nomination is reviewed personally. We verify the need is real and the recipient is legitimate." },
                { num: "4", title: "Pooled Funds Go Out", desc: "When the fund reaches a meaningful amount, we direct it to a verified recipient — because real help takes real money. Every distribution is documented publicly." },
              ].map(step => (
                <div key={step.num} style={{
                  display: "flex", gap: 16, alignItems: "flex-start",
                  padding: "16px 20px", borderRadius: 10,
                  background: "rgba(255,255,255,0.015)",
                  border: "1px solid rgba(255,255,255,0.03)",
                  textAlign: "left",
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%",
                    background: "rgba(180,140,100,0.08)",
                    border: "1px solid rgba(180,140,100,0.2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 26, color: "#c4a87c", flexShrink: 0,
                  }}>
                    {step.num}
                  </div>
                  <div>
                    <div style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: 26, fontWeight: 400, color: "#d4cdc4", marginBottom: 4,
                    }}>
                      {step.title}
                    </div>
                    <div style={{
                      fontFamily: "'Lora', serif", fontSize: 26,
                      color: "rgba(200,190,175,0.45)", lineHeight: 1.6,
                    }}>
                      {step.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Fade>

        {/* ════ CLOSING ════ */}
        <Fade delay={0.3}>
          <div style={{
            marginTop: 48, textAlign: "center",
            padding: "32px 20px",
            borderTop: "1px solid rgba(180,140,100,0.08)",
          }}>
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 26, fontWeight: 300, fontStyle: "italic",
              color: "#c4a87c", lineHeight: 1.5, marginBottom: 16,
              maxWidth: 400, margin: "0 auto 16px",
            }}>
              The best thing about not charging for this tool is that the people 
              who need it most are the ones who can least afford another bill.
            </div>
            <div style={{
              fontFamily: "'Lora', serif", fontSize: 17,
              color: "rgba(180,140,100,0.4)",
            }}>
              — Sara
            </div>
          </div>
        </Fade>
      </div>

      {/* FOOTER */}
      <div style={{
        padding: "20px", borderTop: "1px solid rgba(180,140,100,0.06)",
        textAlign: "center",
      }}>
        <div style={{
          fontFamily: "'Lora', serif", fontSize: 17,
          color: "rgba(180,140,100,0.2)", maxWidth: 500, margin: "0 auto", lineHeight: 1.6,
        }}>
          This project operates on trust and transparency. We are not yet a registered nonprofit, 
          though 501(c)(3) status is planned. Donations are currently gifts, not tax-deductible contributions. 
          All Pet Relief Fund distributions are documented and shared publicly with our community.
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SHARED COMPONENTS & STYLES
// ═══════════════════════════════════════════════════════════════

function FieldLabel({ children }) {
  return (
    <div style={{
      fontFamily: "'Lora', serif",
      fontSize: 17, fontWeight: 500,
      color: "rgba(200,190,175,0.5)",
      marginBottom: 6, marginTop: 14,
      letterSpacing: "0.03em",
    }}>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.06)",
  background: "rgba(255,255,255,0.03)",
  color: "#d4cdc4",
  fontSize: 26,
  fontFamily: "'Lora', serif",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};
